/**
 * Diagnostic Card Component
 * Displays pre-diagnostic results with estimated savings and CTAs
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import type { DiagnosticData } from '../../api/types';

interface DiagnosticCardProps {
  data: DiagnosticData;
  onAction: (action: string) => void;
  isLoading: boolean;
}

const SEVERITY_COLORS = {
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#3B82F6',
};

export default function DiagnosticCard({ data, onAction, isLoading }: DiagnosticCardProps) {
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Title */}
      <Text style={styles.title}>
        {data.user_name}, voici ton pré-diagnostic Wealfi
      </Text>

      {/* Insights */}
      {data.insights.map((insight, index) => (
        <View
          key={index}
          style={[
            styles.insightCard,
            { borderLeftColor: SEVERITY_COLORS[insight.severity] },
          ]}
        >
          <View style={styles.insightHeader}>
            <Text style={styles.insightIcon}>{insight.icon}</Text>
            <Text style={styles.insightTitle}>{insight.title}</Text>
          </View>
          <Text style={styles.insightDescription}>{insight.description}</Text>
        </View>
      ))}

      {/* Separator */}
      <View style={styles.separator} />

      {/* Estimated savings */}
      <Text style={styles.savingsText}>
        {'💰 Potentiel estimé : '}
        <Text style={styles.savingsAmount}>
          {data.estimated_savings.min_yearly} à {data.estimated_savings.max_yearly}€/an
        </Text>
        {" d'économies"}
      </Text>

      {/* Primary message */}
      <Text style={styles.primaryMessage}>{data.primary_message}</Text>

      {/* Separator before CTAs */}
      <View style={styles.separator} />

      {/* Transition title — bridges the gap between estimates and CTAs */}
      <Text style={styles.transitionTitle}>
        {'🎯 '}{data.transition_title}
      </Text>

      {/* CTA Primary — connect bank */}
      <TouchableOpacity
        style={styles.ctaPrimary}
        onPress={() => onAction(data.cta_primary.action)}
        disabled={isLoading}
      >
        <Text style={styles.ctaIcon}>{data.cta_primary.icon}</Text>
        <View style={styles.ctaTextContainer}>
          <Text style={styles.ctaPrimaryLabel}>{data.cta_primary.label}</Text>
          {data.cta_primary.sublabel && (
            <Text style={styles.ctaPrimarySublabel}>{data.cta_primary.sublabel}</Text>
          )}
        </View>
      </TouchableOpacity>

      {/* CTA Secondary — import PDF */}
      <TouchableOpacity
        style={styles.ctaSecondary}
        onPress={() => onAction(data.cta_secondary.action)}
        disabled={isLoading}
      >
        <Text style={styles.ctaIcon}>{data.cta_secondary.icon}</Text>
        <View style={styles.ctaTextContainer}>
          <Text style={styles.ctaSecondaryLabel}>{data.cta_secondary.label}</Text>
          {data.cta_secondary.sublabel && (
            <Text style={styles.ctaSecondarySublabel}>{data.cta_secondary.sublabel}</Text>
          )}
        </View>
      </TouchableOpacity>

      {/* CTA Fallback — manual snapshot */}
      <TouchableOpacity
        style={styles.ctaFallback}
        onPress={() => onAction(data.cta_fallback.action)}
        disabled={isLoading}
      >
        <Text style={styles.ctaIcon}>{data.cta_fallback.icon}</Text>
        <View style={styles.ctaTextContainer}>
          <Text style={styles.ctaFallbackLabel}>{data.cta_fallback.label}</Text>
          {data.cta_fallback.sublabel && (
            <Text style={styles.ctaFallbackSublabel}>{data.cta_fallback.sublabel}</Text>
          )}
        </View>
      </TouchableOpacity>

      {/* Disclaimer — transparency, not a CTA */}
      <Text style={styles.disclaimer}>
        {'⚠️ '}{data.disclaimer}
      </Text>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: spacing.lg,
    paddingBottom: spacing.xl * 2,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  insightCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.md,
    borderLeftWidth: 4,
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  insightIcon: {
    fontSize: 20,
    marginRight: spacing.sm,
  },
  insightTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  insightDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: typography.fontSize.sm * 1.5,
    marginLeft: 28,
  },
  separator: {
    height: 2,
    backgroundColor: colors.border,
    marginVertical: spacing.lg,
  },
  savingsText: {
    fontSize: typography.fontSize.lg,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  savingsAmount: {
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
  },
  primaryMessage: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: typography.fontSize.sm * 1.5,
  },
  transitionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.lg,
  },
  ctaIcon: {
    fontSize: 22,
    marginRight: spacing.md,
  },
  ctaTextContainer: {
    flex: 1,
  },
  ctaPrimary: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingVertical: spacing.md + 2,
    paddingHorizontal: spacing.lg,
    borderRadius: 12,
    marginBottom: spacing.md,
  },
  ctaPrimaryLabel: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.button.primaryText,
  },
  ctaPrimarySublabel: {
    fontSize: typography.fontSize.xs,
    color: colors.button.primaryText,
    opacity: 0.85,
    marginTop: 2,
  },
  ctaSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: colors.primary,
    marginBottom: spacing.md,
  },
  ctaSecondaryLabel: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.primary,
  },
  ctaSecondarySublabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 2,
  },
  ctaFallback: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.lg,
  },
  ctaFallbackLabel: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
  ctaFallbackSublabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textTertiary,
    marginTop: 2,
  },
  disclaimer: {
    fontSize: typography.fontSize.xs,
    color: colors.textTertiary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
});
